import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';
import { store } from 'redux/store';
import {
  TradingOffer,
  TradingOfferType,
  TradingOffersSummary,
} from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';

import { EquityMarketAdminPanelContextType } from '../types/EquityMarketAdminPanelContextType';
import { equityMarketAdminPanelInitValue } from '../constants/EquityMarketAdminPanelInitValue';
import { useGetTradingOffers } from '../hooks/useGetTradingOffers';
import {
  getTradingOfferOperations,
  updateTradingOfferOperation,
} from '../redux/tradingOfferOperationActions';

export const EquityMarketAdminPanelContext =
  createContext<EquityMarketAdminPanelContextType>(
    equityMarketAdminPanelInitValue
  );

const TRADING_OFFERS_PAGE_SIZE = 100;
const TRADING_OFFERS_FILTERS: string[] = [];

export const EquityMarketAdminPanelProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams<{ societyId: string }>();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partnerId = queryParams.get('partnerId');
  const partnerQuery = partnerId ? `?partnerId=${partnerId}` : '';

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );
  const tradingOffers = useSelector(
    ({ tradingOffers }: State) => (tradingOffers ?? []) as TradingOffer[]
  );

  const [operations, setOperations] = useState<TradingOfferOperation[]>([]);
  const [isOperationsLoading, setIsOperationsLoading] = useState(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const { isLoading: isTradingOffersLoading } = useGetTradingOffers({
    societyId: actualSociety ? societyId : undefined,
    page: 1,
    rowsPerPage: TRADING_OFFERS_PAGE_SIZE,
    filters: TRADING_OFFERS_FILTERS,
  });

  useEffect(() => {
    if (!actualSociety || !societyId) return;

    const controller = new AbortController();

    const fetchOperations = async () => {
      try {
        setIsOperationsLoading(true);
        const result = await store.dispatch(
          getTradingOfferOperations({ societyId })
        );
        if (!controller.signal.aborted && result) {
          setOperations(result.data || []);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsOperationsLoading(false);
        }
      }
    };

    fetchOperations();

    return () => controller.abort();
  }, [actualSociety, societyId]);

  const sellOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.SELL),
    [tradingOffers]
  );

  const buyOffers = useMemo(
    () => tradingOffers.filter((offer) => offer.type === TradingOfferType.BUY),
    [tradingOffers]
  );

  const calculateSummary = useCallback(
    (offers: TradingOffer[]): TradingOffersSummary => {
      const offersCount = offers.length;
      const sharesCount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total,
        0
      );
      const totalAmount = offers.reduce(
        (acc, offer) => acc + offer.sharesCount.total * offer.sharePrice,
        0
      );
      return { offersCount, sharesCount, totalAmount };
    },
    []
  );

  const sellOffersSummary = useMemo(
    () => calculateSummary(sellOffers),
    [sellOffers, calculateSummary]
  );

  const buyOffersSummary = useMemo(
    () => calculateSummary(buyOffers),
    [buyOffers, calculateSummary]
  );

  const pendingOperationsCount = useMemo(
    () =>
      operations.filter(
        (op) =>
          op.status !== 'COMPLETED' &&
          op.status !== 'CANCELLED' &&
          op.status !== 'REJECTED'
      ).length,
    [operations]
  );

  const handleGoBack = useCallback(() => {
    history.push(`/mercado-de-acciones/${societyId}${partnerQuery}`);
  }, [history, societyId, partnerQuery]);

  const handleViewOfferDetail = useCallback(
    (offerId: string) => {
      history.push(`/detalle-oferta/${societyId}/${offerId}${partnerQuery}`);
    },
    [history, societyId, partnerQuery]
  );

  const handleViewOperationDetail = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-oferta/${societyId}/${operationId}${partnerQuery}`
      );
    },
    [history, societyId, partnerQuery]
  );

  const handleUpdateOperationStatus = useCallback(
    async (operationId: string, status: string, comment?: string) => {
      await dispatch(
        updateTradingOfferOperation(operationId, { status, comment } as any)
      );
      const result = await store.dispatch(
        getTradingOfferOperations({ societyId })
      );
      if (result) {
        setOperations(result.data || []);
      }
    },
    [dispatch, societyId]
  );

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        sellOffers,
        buyOffers,
        operations,
        sellOffersSummary,
        buyOffersSummary,
        pendingOperationsCount,
        isAdmin,
        isLoading:
          isTradingOffersLoading || isOperationsLoading || !actualSociety,
      },
      actions: {
        handleGoBack,
        handleViewOfferDetail,
        handleViewOperationDetail,
        handleUpdateOperationStatus,
      },
    }),
    [
      societyId,
      actualSociety,
      sellOffers,
      buyOffers,
      operations,
      sellOffersSummary,
      buyOffersSummary,
      pendingOperationsCount,
      isAdmin,
      isTradingOffersLoading,
      isOperationsLoading,
      handleGoBack,
      handleViewOfferDetail,
      handleViewOperationDetail,
      handleUpdateOperationStatus,
    ]
  );

  return (
    <EquityMarketAdminPanelContext.Provider value={contextValue}>
      {children}
    </EquityMarketAdminPanelContext.Provider>
  );
};

export const useEquityMarketAdminPanel = () => {
  const context = useContext(EquityMarketAdminPanelContext);
  if (!context) {
    throw new Error(
      'useEquityMarketAdminPanel must be used within an EquityMarketAdminPanelProvider'
    );
  }
  return context;
};
